import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams, useSearchParams, useOutletContext, useNavigate, useLocation } from "react-router";
import { ThreeDot } from "react-loading-indicators";
import { parse as parseDate, format as formatDate } from "date-fns";

import FilterCheckboxList from "./components/filterCheckboxList";
import FilterRadioList from "./components/filterRadioList.jsx";
import FilterStarRating from "./components/filterStarRating.jsx";
import FilterTripAdvisor from "./components/filterTripAdvisor.jsx";
import FilterFlightDuration from "./components/filterFlightDuration.jsx";
import FilterAirlines from "./components/filterAirlines.jsx";
import FilterToggle from "./components/filterToggle.jsx";
import { useCsvParameters } from "/src/helpers/Hooks/useCsvParameters.jsx";
import LoadingWrapper from "/src/helpers/Wrappers/LoadingWrapper.jsx";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { PricingDisplayMethodContext } from "/src/helpers/contexts/agentContext.jsx";
import { SALES_PHONE } from "/src/config/constants.js";
import { DebugModeContext } from "/src/helpers/contexts/agentContext";
import { pushEcommerce } from "/src/helpers/analytics.js";

export default function BaseSearchPage({ resultFetcher, resultTemplate, searchFormBuilder, listName = 'Package Holiday Results', showFlightDurationFilter = false, showExtraFilters = false }) {
    const [data, setData] = useState();
    const [isLoadingFiltering, setIsLoadingFiltering] = useState();
    const [filterOpened, setFilterOpened] = useState(false);
    const [datesBannerDismissed, setDatesBannerDismissed] = useState(false);
    const [withdrawnOpen, setWithdrawnOpen] = useState(false);
    const ctx = useOutletContext();
    const [debugMode, umbracoAdminUrl] = useContext(DebugModeContext);

    const searchParams = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [pricingDisplayMethod, setPricingDisplayMethod] = useContext(PricingDisplayMethodContext);

    const isLoadingMore = useRef(false);
    // Raw (not dash-split) parameters: "sort" (the Recommended sort id is -1, which would turn into ["", "1"]
    // and 400 the search API) and "transfer-type" (its slugs contain dashes - "multi-leg" - and as a radio it
    // is always one value).
    const [filterParams, addValue, removeValue, setValue, deleteValue] = useCsvParameters(["sort", "transfer-type"]);

    useEffect(function () {
        document.title = "Olympic Holidays Search Results";
    }, []);

    useEffect(
        function () {
            const model = searchFormBuilder(searchParams);
            ctx.setDefaultSearch(model);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resync the default search only when the route changes
        [searchParams]
    );

    // Re-run the search whenever the filter set changes. Keyed on the serialised
    // filters (extracted so the dependency can be statically checked); the other
    // referenced values are derived from the route and change together with it.
    const filterParamsKey = JSON.stringify(filterParams);
    useEffect(() => {
        //  setData(null)
        setIsLoadingFiltering(true);
        async function fetchData() {
            isLoadingMore.current = true;
            const urlParams = JSON.parse(JSON.stringify(searchParams));
            const rooms = searchParams.rooms.split("-");
            urlParams.rooms = rooms;
            delete urlParams["*"];
            const locdata = await resultFetcher(urlParams, filterParams);
            locdata.lastResponseWasFull =
                locdata.offers.length + locdata.offersWithdrawn.length == 10;
            locdata.recommendedExhausted = locdata.recommendedExhausted || false;
            trackResults(locdata.offers);
            isLoadingMore.current = false;
            setData(locdata);
            setIsLoadingFiltering(false);

            // The API shifted a past search window forward — reflect the adjusted dates in the
            // URL (and thereby the header search form) so refreshes and shares carry the dates
            // actually searched. Both search routes have adjacent /{earlyStart}/{lateStart}/
            // segments, so a targeted path replace works for package and hotel-only alike.
            if (locdata.datesAdjusted && searchParams.earlyStart !== locdata.adjustedEarlyStart) {
                const newPath = window.location.pathname.replace(
                    `/${searchParams.earlyStart}/${searchParams.lateStart}`,
                    `/${locdata.adjustedEarlyStart}/${locdata.adjustedLateStart}`
                );
                navigate(newPath + location.search, { replace: true });
            }
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterParamsKey]);

    const trackResults = (results) => {
        window.dataLayer = window.dataLayer || [];
        const items = results.map((x, i) => {
            return {
                item_id: x.hotel.id,
                item_name: x.hotel.name,
                item_category: x.countryName + "|" + x.regionName + "|" + x.resortName,
                departure_airport: x.flightOutbound?.departureAirport?.iataCode,
                check_in: x.flightOutbound?.departureDate ?? x.startDate,
                check_out: x.flightInbound?.departureDate,
            };
        });

        pushEcommerce({
            event: "view_item_list",
            ecommerce: {
                item_list_name: listName, // e.g., 'Package Holiday Results', 'Hotel Only Results'
                items: items,
            },
        });
    };

    const hasMore =
        data?.offerCount > data?.offers?.length + data?.offersWithdrawn.length &&
        data?.lastResponseWasFull;
    const [infiniteRef] = useInfiniteScroll({
        loading: false,
        hasNextPage: hasMore,
        onLoadMore: () => {
            async function fetchData() {
                const urlParams = JSON.parse(JSON.stringify(searchParams));
                const rooms = searchParams.rooms.split("-");
                urlParams.rooms = rooms;
                delete urlParams["*"];
                const partialUpdate = await resultFetcher(
                    urlParams,
                    filterParams,
                    data.minRank,
                    data.recommendedExhausted
                );
                const newModel = { ...data };

                newModel.minRank = partialUpdate.minRank;
                newModel.offersWithdrawn = [
                    ...data.offersWithdrawn,
                    ...partialUpdate.offersWithdrawn,
                ];
                newModel.offers = [...data.offers, ...partialUpdate.offers];
                newModel.lastResponseWasFull =
                    partialUpdate.offers.length + partialUpdate.offersWithdrawn.length == 10;
                newModel.recommendedExhausted = partialUpdate.recommendedExhausted || false;
                newModel.searchSource = partialUpdate.searchSource;
                newModel.withdrawnReasons = {
                    ...data.withdrawnReasons,
                    ...partialUpdate.withdrawnReasons,
                };

                trackResults(partialUpdate.offers);

                setData(newModel);
            }
            fetchData();
        },
        rootMargin: "0px 0px 400px 0px",
    });

    const filterEvents = {
        addOption: addValue,
        removeOption: removeValue,
        setOption: setValue,
        // Scalar read for single-value filters (e.g. the flight-time range handles).
        getValue: (key) => {
            const parameter = filterParams[key];
            return Array.isArray(parameter) ? parameter[0] : parameter;
        },
        // Multi-value read/write for the airline filter (all-selected-by-default semantics).
        getValues: (key) => {
            const parameter = filterParams[key];
            if (parameter == null) return [];
            return Array.isArray(parameter) ? parameter : [parameter];
        },
        setValues: (key, values) => setValue(key, [...values].sort().join("-")),
        clearValue: (key) => deleteValue(key),
        isSelected: (facet, option) => {
            const parameter = filterParams[facet];
            if (!parameter) {
                return false;
            }
            return parameter.includes(option.toString());
        },
    };

    // Sidebar order is deliberate and ends: Extras, Airlines, Flight time. Airlines and Transfer type are
    // server-driven facets like the rest, but they're rendered out of band so they can sit in that order —
    // Transfer type inside Extras (same subject as the transfers toggle), Airlines on its own after it.
    // Property source is a diagnostic and renders only inside the DEBUG block.
    const TAIL_FACET_KEYS = ["airlines", "transfer-type", "property-source"];
    const isMainFacet = (section) => section && !TAIL_FACET_KEYS.includes(section.key);
    const airlineFacet = data?.facets?.find((f) => f?.key === "airlines");
    const transferTypeFacet = data?.facets?.find((f) => f?.key === "transfer-type");
    const propertySourceFacet = data?.facets?.find((f) => f?.key === "property-source");
    const transfersIncludedOn = filterEvents.getValue("transfers-included") === "true";

    const buildFilters = (section) => {
        if (section.renderType == "CheckboxList") {
            return <FilterCheckboxList model={section} events={filterEvents} />;
        } else if (section.renderType == "RadioList") {
            return <FilterRadioList model={section} events={filterEvents} />;
        } else if (section.renderType == "StarRating") {
            return <FilterStarRating model={section} events={filterEvents} />;
        } else if (section.renderType == "TripAdvisorRating") {
            return <FilterTripAdvisor model={section} events={filterEvents} />;
        } else if (section.renderType == "SearchableCheckboxList") {
            return <FilterCheckboxList model={section} events={filterEvents} />;
        } else if (section.renderType == "AirlineList") {
            return <FilterAirlines model={section} events={filterEvents} />;
        }
        return <h1>unknown: {section.renderType}</h1>;
    };

    return (
        <div className={debugMode ? "pb-8" : undefined}>
            <div className="flex gap-0 lg:gap-6 mb-6">
                <div
                    id="filter-component"
                    className={
                        "bg-white  fixed right-0 top-0 h-full w-1/1 z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out " +
                        "lg:rounded-2xl lg:w-80 lg:sticky lg:top-auto lg:right-auto lg:h-full  lg:transform-none lg:transition-none lg:z-auto lg:overflow-visible " +
                        (filterOpened ? "translate-x-0" : "translate-x-full lg:translate-x-0")
                    }
                >
                    <div className="px-6 py-4 overflow-hidden">
                        <LoadingWrapper isLoading={!data}>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M2.25 4.5C1.83579 4.5 1.5 4.83579 1.5 5.25C1.5 5.66421 1.83579 6 2.25 6V4.5ZM2.25 12C1.83579 12 1.5 12.3358 1.5 12.75C1.5 13.1642 1.83579 13.5 2.25 13.5V12ZM6.75 13.5C7.16421 13.5 7.5 13.1642 7.5 12.75C7.5 12.3358 7.16421 12 6.75 12V13.5ZM15.75 13.5C16.1642 13.5 16.5 13.1642 16.5 12.75C16.5 12.3358 16.1642 12 15.75 12V13.5ZM11.25 4.5C10.8358 4.5 10.5 4.83579 10.5 5.25C10.5 5.66421 10.8358 6 11.25 6V4.5ZM15.75 6C16.1642 6 16.5 5.66421 16.5 5.25C16.5 4.83579 16.1642 4.5 15.75 4.5V6ZM4.61418 3.92597L3.92127 3.63895L3.92127 3.63897L4.61418 3.92597ZM5.42597 3.11418L5.13897 2.42127L5.13895 2.42127L5.42597 3.11418ZM8.07405 3.11418L8.36109 2.42128L8.36102 2.42125L8.07405 3.11418ZM8.88585 3.92597L9.57879 3.63903L9.57875 3.63894L8.88585 3.92597ZM8.88585 6.57403L9.57875 6.86106L9.57879 6.86097L8.88585 6.57403ZM8.07405 7.38582L8.36102 8.07875L8.36109 8.07872L8.07405 7.38582ZM5.42597 7.38582L5.13895 8.07873L5.13897 8.07873L5.42597 7.38582ZM4.61418 6.57403L3.92127 6.86103L3.92127 6.86105L4.61418 6.57403ZM9.11415 11.426L8.42126 11.1389L8.4212 11.139L9.11415 11.426ZM9.92595 10.6142L9.63904 9.9212L9.6389 9.92126L9.92595 10.6142ZM12.574 10.6142L12.8611 9.92126L12.861 9.9212L12.574 10.6142ZM13.3858 11.426L14.0788 11.139L14.0787 11.1389L13.3858 11.426ZM13.3858 14.074L14.0787 14.3611L14.0788 14.361L13.3858 14.074ZM12.574 14.8858L12.861 15.5788L12.8611 15.5787L12.574 14.8858ZM9.92595 14.8858L9.6389 15.5787L9.63904 15.5788L9.92595 14.8858ZM9.11415 14.074L8.4212 14.361L8.42126 14.3611L9.11415 14.074ZM2.25 5.25V6H4.5V5.25V4.5H2.25V5.25ZM2.25 12.75V13.5H6.75V12.75V12H2.25V12.75ZM13.5 12.75V13.5H15.75V12.75V12H13.5V12.75ZM11.25 5.25V6H15.75V5.25V4.5H11.25V5.25ZM4.5 5.25H5.25C5.25 4.89028 5.25041 4.65806 5.26253 4.48032C5.2742 4.30932 5.29416 4.2442 5.30709 4.21298L4.61418 3.92597L3.92127 3.63897C3.82002 3.88341 3.78289 4.13084 3.76601 4.37822C3.74959 4.61885 3.75 4.91081 3.75 5.25H4.5ZM4.61418 3.92597L5.30709 4.21299C5.38321 4.02922 5.52922 3.88321 5.71299 3.80709L5.42597 3.11418L5.13895 2.42127C4.58765 2.64964 4.14964 3.08765 3.92127 3.63895L4.61418 3.92597ZM5.42597 3.11418L5.71298 3.80709C5.7442 3.79416 5.80932 3.7742 5.98032 3.76253C6.15806 3.75041 6.39028 3.75 6.75 3.75V3V2.25C6.41081 2.25 6.11885 2.24959 5.87822 2.26601C5.63084 2.28289 5.38341 2.32002 5.13897 2.42127L5.42597 3.11418ZM6.75 3V3.75C7.10972 3.75 7.34194 3.75041 7.51968 3.76253C7.69067 3.7742 7.75582 3.79416 7.78708 3.80711L8.07405 3.11418L8.36102 2.42125C8.11658 2.32002 7.86916 2.28289 7.62178 2.26601C7.38115 2.24959 7.08919 2.25 6.75 2.25V3ZM8.07405 3.11418L7.78701 3.80708C7.97079 3.88321 8.11683 4.02925 8.19295 4.213L8.88585 3.92597L9.57875 3.63894C9.35037 3.08762 8.91231 2.64963 8.36109 2.42128L8.07405 3.11418ZM8.88585 3.92597L8.19291 4.21291C8.20585 4.24415 8.22581 4.3093 8.23747 4.48031C8.24959 4.65805 8.25 4.89029 8.25 5.25H9H9.75C9.75 4.9108 9.75041 4.61886 9.73399 4.37823C9.71712 4.13087 9.68 3.88346 9.57879 3.63903L8.88585 3.92597ZM9 5.25H8.25C8.25 5.60971 8.24959 5.84195 8.23747 6.01969C8.22581 6.1907 8.20585 6.25585 8.19291 6.28709L8.88585 6.57403L9.57879 6.86097C9.68001 6.61654 9.71712 6.36913 9.73399 6.12177C9.75041 5.88114 9.75 5.5892 9.75 5.25H9ZM8.88585 6.57403L8.19295 6.287C8.11683 6.47075 7.97079 6.61679 7.78701 6.69292L8.07405 7.38582L8.36109 8.07872C8.91231 7.85037 9.35037 7.41238 9.57875 6.86106L8.88585 6.57403ZM8.07405 7.38582L7.78708 6.69289C7.75582 6.70584 7.69067 6.7258 7.51968 6.73747C7.34194 6.74959 7.10972 6.75 6.75 6.75V7.5V8.25C7.08919 8.25 7.38115 8.25041 7.62178 8.23399C7.86916 8.21711 8.11658 8.17998 8.36102 8.07875L8.07405 7.38582ZM6.75 7.5V6.75C6.39028 6.75 6.15806 6.74959 5.98032 6.73747C5.80932 6.7258 5.7442 6.70584 5.71298 6.69291L5.42597 7.38582L5.13897 8.07873C5.38341 8.17998 5.63084 8.21711 5.87822 8.23399C6.11885 8.25041 6.41081 8.25 6.75 8.25V7.5ZM5.42597 7.38582L5.71299 6.69291C5.52922 6.61679 5.38321 6.47078 5.30709 6.28701L4.61418 6.57403L3.92127 6.86105C4.14964 7.41235 4.58765 7.85036 5.13895 8.07873L5.42597 7.38582ZM4.61418 6.57403L5.30709 6.28702C5.29416 6.25581 5.2742 6.19068 5.26253 6.01968C5.25041 5.84194 5.25 5.60972 5.25 5.25H4.5H3.75C3.75 5.58919 3.74959 5.88115 3.76601 6.12178C3.78289 6.36916 3.82002 6.61659 3.92127 6.86103L4.61418 6.57403ZM9 12.75H9.75C9.75 12.3903 9.75041 12.158 9.76253 11.9803C9.77419 11.8093 9.79415 11.7441 9.8071 11.7129L9.11415 11.426L8.4212 11.139C8.32 11.3835 8.28288 11.6309 8.26601 11.8782C8.24959 12.1189 8.25 12.4108 8.25 12.75H9ZM9.11415 11.426L9.80704 11.713C9.88317 11.5292 10.0292 11.3832 10.213 11.307L9.92595 10.6142L9.6389 9.92126C9.08766 10.1496 8.64963 10.5877 8.42126 11.1389L9.11415 11.426ZM9.92595 10.6142L10.2129 11.3071C10.2441 11.2942 10.3093 11.2742 10.4803 11.2625C10.658 11.2504 10.8903 11.25 11.25 11.25V10.5V9.75C10.9108 9.75 10.6189 9.74959 10.3782 9.76601C10.1309 9.78288 9.88346 9.82 9.63904 9.9212L9.92595 10.6142ZM11.25 10.5V11.25C11.6097 11.25 11.842 11.2504 12.0197 11.2625C12.1907 11.2742 12.2559 11.2942 12.2871 11.3071L12.574 10.6142L12.861 9.9212C12.6165 9.82 12.3691 9.78288 12.1218 9.76601C11.8811 9.74959 11.5892 9.75 11.25 9.75V10.5ZM12.574 10.6142L12.287 11.307C12.4708 11.3832 12.6168 11.5292 12.693 11.713L13.3858 11.426L14.0787 11.1389C13.8504 10.5877 13.4123 10.1496 12.8611 9.92126L12.574 10.6142ZM13.3858 11.426L12.6929 11.7129C12.7058 11.7441 12.7258 11.8093 12.7375 11.9803C12.7496 12.158 12.75 12.3903 12.75 12.75H13.5H14.25C14.25 12.4108 14.2504 12.1189 14.234 11.8782C14.2171 11.6309 14.18 11.3835 14.0788 11.139L13.3858 11.426ZM13.5 12.75H12.75C12.75 13.1097 12.7496 13.342 12.7375 13.5197C12.7258 13.6907 12.7058 13.7559 12.6929 13.7871L13.3858 14.074L14.0788 14.361C14.18 14.1165 14.2171 13.8691 14.234 13.6218C14.2504 13.3811 14.25 13.0892 14.25 12.75H13.5ZM13.3858 14.074L12.693 13.787C12.6168 13.9708 12.4708 14.1168 12.287 14.193L12.574 14.8858L12.8611 15.5787C13.4123 15.3504 13.8504 14.9123 14.0787 14.3611L13.3858 14.074ZM12.574 14.8858L12.2871 14.1929C12.2559 14.2058 12.1907 14.2258 12.0197 14.2375C11.842 14.2496 11.6097 14.25 11.25 14.25V15V15.75C11.5892 15.75 11.8811 15.7504 12.1218 15.734C12.3691 15.7171 12.6165 15.68 12.861 15.5788L12.574 14.8858ZM11.25 15V14.25C10.8903 14.25 10.658 14.2496 10.4803 14.2375C10.3093 14.2258 10.2441 14.2058 10.2129 14.1929L9.92595 14.8858L9.63904 15.5788C9.88346 15.68 10.1309 15.7171 10.3782 15.734C10.6189 15.7504 10.9108 15.75 11.25 15.75V15ZM9.92595 14.8858L10.213 14.193C10.0292 14.1168 9.88317 13.9708 9.80704 13.787L9.11415 14.074L8.42126 14.3611C8.64963 14.9123 9.08766 15.3504 9.6389 15.5787L9.92595 14.8858ZM9.11415 14.074L9.8071 13.7871C9.79415 13.7559 9.77419 13.6907 9.76253 13.5197C9.75041 13.342 9.75 13.1097 9.75 12.75H9H8.25C8.25 13.0892 8.24959 13.3811 8.26601 13.6218C8.28288 13.8691 8.32 14.1165 8.4212 14.361L9.11415 14.074Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    Filters
                                </h2>
                            </div>

                            {showExtraFilters && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        <svg width="6" height="24" viewBox="0 0 6 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="3" cy="12" r="2.25" stroke="#8DA0A2" strokeWidth="1.5" />
                                        </svg>
                                        Extras
                                    </h3>
                                    <div className="space-y-2">
                                        {/* A price lens, not a filter: transfers are available almost
                                            everywhere, so this prices every result WITH its cheapest one
                                            rather than hiding the resorts where it's optional. */}
                                        <FilterToggle
                                            label="Include Transfers"
                                            description="Show prices with transfers included"
                                            paramKey="transfers-included"
                                            events={filterEvents}
                                        />
                                        {/* Nested under Include Transfers: that toggle decides whether a
                                            transfer is in the price at all, and this refines WHICH one, so
                                            it means nothing on its own. (It was briefly ungated while the
                                            toggle was still a filter over mandatory-transfer resorts — then
                                            the two genuinely did contradict each other.) */}
                                        {transfersIncludedOn && transferTypeFacet && (
                                            <div className="pl-7 pt-1 pb-1">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">
                                                    {transferTypeFacet.name}
                                                </p>
                                                <div className="space-y-2">{buildFilters(transferTypeFacet)}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {airlineFacet && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        <svg width="6" height="24" viewBox="0 0 6 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="3" cy="12" r="2.25" stroke="#8DA0A2" strokeWidth="1.5" />
                                        </svg>
                                        {airlineFacet.name}
                                    </h3>
                                    <div className="space-y-2">{buildFilters(airlineFacet)}</div>
                                </div>
                            )}
                            {showFlightDurationFilter && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        <svg width="6" height="24" viewBox="0 0 6 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="3" cy="12" r="2.25" stroke="#8DA0A2" strokeWidth="1.5" />
                                        </svg>
                                        Flight time
                                    </h3>
                                    <FilterFlightDuration
                                        minKey="flight-duration-min"
                                        maxKey="flight-duration-max"
                                        events={filterEvents}
                                    />
                                </div>
                            )}
                            {data?.facets?.filter(isMainFacet).map((section, i) => (
                                <div className="mb-6" key={section.name}>
                                    <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        <svg
                                            width="6"
                                            height="24"
                                            viewBox="0 0 6 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle
                                                cx="3"
                                                cy="12"
                                                r="2.25"
                                                stroke="#8DA0A2"
                                                strokeWidth="1.5"
                                            />
                                        </svg>
                                        {section.name}
                                    </h3>
                                    <div className="space-y-2">{buildFilters(section)}</div>
                                </div>
                            ))}
                            {debugMode && (
                                <div className="mb-6 bg-olympic_main mx-[-1.5rem] px-[1.5rem]">
                                    <h3 className="text-lg font-bold text-gray-700 mb-3  text-center">
                                        DEBUG
                                    </h3>
                                    {/* Diagnostic source filter — the tinted background says this isn't a
                                        customer control. It still round-trips through the URL like any
                                        filter, so a shared link reproduces the same narrowed view. */}
                                    {propertySourceFacet && (
                                        <div className="mb-6">
                                            <h4 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                                {propertySourceFacet.name}
                                            </h4>
                                            <div className="space-y-2">
                                                {buildFilters(propertySourceFacet)}
                                            </div>
                                        </div>
                                    )}
                                    <h4 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        Withdrawn Offers CPRs
                                    </h4>
                                    <div className="space-y-2 flex flex-wrap">
                                        {data?.offersWithdrawn?.map((cpr, i) => {
                                            return (
                                                <span
                                                    className="p-1 m-1 bg-red rounded"
                                                    key={i}
                                                    title={data.withdrawnReasons?.[cpr]}
                                                >
                                                    {cpr}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={(e) => setFilterOpened(false)}
                                className="btn-secondary w-1/1 p-2 lg:hidden"
                            >
                                Close
                            </button>
                        </LoadingWrapper>
                    </div>
                </div>
                <div id="result-cards" className="flex-1">
                    <div className="block pb-3 lg:hidden text-center">
                        <button
                            onClick={() => setFilterOpened(!filterOpened)}
                            type="button"
                            className="btn-secondary h-[2.625rem] inline-flex items-center gap-2 text-secondary"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2.25 4.5C1.83579 4.5 1.5 4.83579 1.5 5.25C1.5 5.66421 1.83579 6 2.25 6V4.5ZM2.25 12C1.83579 12 1.5 12.3358 1.5 12.75C1.5 13.1642 1.83579 13.5 2.25 13.5V12ZM6.75 13.5C7.16421 13.5 7.5 13.1642 7.5 12.75C7.5 12.3358 7.16421 12 6.75 12V13.5ZM15.75 13.5C16.1642 13.5 16.5 13.1642 16.5 12.75C16.5 12.3358 16.1642 12 15.75 12V13.5ZM11.25 4.5C10.8358 4.5 10.5 4.83579 10.5 5.25C10.5 5.66421 10.8358 6 11.25 6V4.5ZM15.75 6C16.1642 6 16.5 5.66421 16.5 5.25C16.5 4.83579 16.1642 4.5 15.75 4.5V6ZM4.61418 3.92597L3.92127 3.63895L3.92127 3.63897L4.61418 3.92597ZM5.42597 3.11418L5.13897 2.42127L5.13895 2.42127L5.42597 3.11418ZM8.07405 3.11418L8.36109 2.42128L8.36102 2.42125L8.07405 3.11418ZM8.88585 3.92597L9.57879 3.63903L9.57875 3.63894L8.88585 3.92597ZM8.88585 6.57403L9.57875 6.86106L9.57879 6.86097L8.88585 6.57403ZM8.07405 7.38582L8.36102 8.07875L8.36109 8.07872L8.07405 7.38582ZM5.42597 7.38582L5.13895 8.07873L5.13897 8.07873L5.42597 7.38582ZM4.61418 6.57403L3.92127 6.86103L3.92127 6.86105L4.61418 6.57403ZM9.11415 11.426L8.42126 11.1389L8.4212 11.139L9.11415 11.426ZM9.92595 10.6142L9.63904 9.9212L9.6389 9.92126L9.92595 10.6142ZM12.574 10.6142L12.8611 9.92126L12.861 9.9212L12.574 10.6142ZM13.3858 11.426L14.0788 11.139L14.0787 11.1389L13.3858 11.426ZM13.3858 14.074L14.0787 14.3611L14.0788 14.361L13.3858 14.074ZM12.574 14.8858L12.861 15.5788L12.8611 15.5787L12.574 14.8858ZM9.92595 14.8858L9.6389 15.5787L9.63904 15.5788L9.92595 14.8858ZM9.11415 14.074L8.4212 14.361L8.42126 14.3611L9.11415 14.074ZM2.25 5.25V6H4.5V5.25V4.5H2.25V5.25ZM2.25 12.75V13.5H6.75V12.75V12H2.25V12.75ZM13.5 12.75V13.5H15.75V12.75V12H13.5V12.75ZM11.25 5.25V6H15.75V5.25V4.5H11.25V5.25ZM4.5 5.25H5.25C5.25 4.89028 5.25041 4.65806 5.26253 4.48032C5.2742 4.30932 5.29416 4.2442 5.30709 4.21298L4.61418 3.92597L3.92127 3.63897C3.82002 3.88341 3.78289 4.13084 3.76601 4.37822C3.74959 4.61885 3.75 4.91081 3.75 5.25H4.5ZM4.61418 3.92597L5.30709 4.21299C5.38321 4.02922 5.52922 3.88321 5.71299 3.80709L5.42597 3.11418L5.13895 2.42127C4.58765 2.64964 4.14964 3.08765 3.92127 3.63895L4.61418 3.92597ZM5.42597 3.11418L5.71298 3.80709C5.7442 3.79416 5.80932 3.7742 5.98032 3.76253C6.15806 3.75041 6.39028 3.75 6.75 3.75V3V2.25C6.41081 2.25 6.11885 2.24959 5.87822 2.26601C5.63084 2.28289 5.38341 2.32002 5.13897 2.42127L5.42597 3.11418ZM6.75 3V3.75C7.10972 3.75 7.34194 3.75041 7.51968 3.76253C7.69067 3.7742 7.75582 3.79416 7.78708 3.80711L8.07405 3.11418L8.36102 2.42125C8.11658 2.32002 7.86916 2.28289 7.62178 2.26601C7.38115 2.24959 7.08919 2.25 6.75 2.25V3ZM8.07405 3.11418L7.78701 3.80708C7.97079 3.88321 8.11683 4.02925 8.19295 4.213L8.88585 3.92597L9.57875 3.63894C9.35037 3.08762 8.91231 2.64963 8.36109 2.42128L8.07405 3.11418ZM8.88585 3.92597L8.19291 4.21291C8.20585 4.24415 8.22581 4.3093 8.23747 4.48031C8.24959 4.65805 8.25 4.89029 8.25 5.25H9H9.75C9.75 4.9108 9.75041 4.61886 9.73399 4.37823C9.71712 4.13087 9.68 3.88346 9.57879 3.63903L8.88585 3.92597ZM9 5.25H8.25C8.25 5.60971 8.24959 5.84195 8.23747 6.01969C8.22581 6.1907 8.20585 6.25585 8.19291 6.28709L8.88585 6.57403L9.57879 6.86097C9.68001 6.61654 9.71712 6.36913 9.73399 6.12177C9.75041 5.88114 9.75 5.5892 9.75 5.25H9ZM8.88585 6.57403L8.19295 6.287C8.11683 6.47075 7.97079 6.61679 7.78701 6.69292L8.07405 7.38582L8.36109 8.07872C8.91231 7.85037 9.35037 7.41238 9.57875 6.86106L8.88585 6.57403ZM8.07405 7.38582L7.78708 6.69289C7.75582 6.70584 7.69067 6.7258 7.51968 6.73747C7.34194 6.74959 7.10972 6.75 6.75 6.75V7.5V8.25C7.08919 8.25 7.38115 8.25041 7.62178 8.23399C7.86916 8.21711 8.11658 8.17998 8.36102 8.07875L8.07405 7.38582ZM6.75 7.5V6.75C6.39028 6.75 6.15806 6.74959 5.98032 6.73747C5.80932 6.7258 5.7442 6.70584 5.71298 6.69291L5.42597 7.38582L5.13897 8.07873C5.38341 8.17998 5.63084 8.21711 5.87822 8.23399C6.11885 8.25041 6.41081 8.25 6.75 8.25V7.5ZM5.42597 7.38582L5.71299 6.69291C5.52922 6.61679 5.38321 6.47078 5.30709 6.28701L4.61418 6.57403L3.92127 6.86105C4.14964 7.41235 4.58765 7.85036 5.13895 8.07873L5.42597 7.38582ZM4.61418 6.57403L5.30709 6.28702C5.29416 6.25581 5.2742 6.19068 5.26253 6.01968C5.25041 5.84194 5.25 5.60972 5.25 5.25H4.5H3.75C3.75 5.58919 3.74959 5.88115 3.76601 6.12178C3.78289 6.36916 3.82002 6.61659 3.92127 6.86103L4.61418 6.57403ZM9 12.75H9.75C9.75 12.3903 9.75041 12.158 9.76253 11.9803C9.77419 11.8093 9.79415 11.7441 9.8071 11.7129L9.11415 11.426L8.4212 11.139C8.32 11.3835 8.28288 11.6309 8.26601 11.8782C8.24959 12.1189 8.25 12.4108 8.25 12.75H9ZM9.11415 11.426L9.80704 11.713C9.88317 11.5292 10.0292 11.3832 10.213 11.307L9.92595 10.6142L9.6389 9.92126C9.08766 10.1496 8.64963 10.5877 8.42126 11.1389L9.11415 11.426ZM9.92595 10.6142L10.2129 11.3071C10.2441 11.2942 10.3093 11.2742 10.4803 11.2625C10.658 11.2504 10.8903 11.25 11.25 11.25V10.5V9.75C10.9108 9.75 10.6189 9.74959 10.3782 9.76601C10.1309 9.78288 9.88346 9.82 9.63904 9.9212L9.92595 10.6142ZM11.25 10.5V11.25C11.6097 11.25 11.842 11.2504 12.0197 11.2625C12.1907 11.2742 12.2559 11.2942 12.2871 11.3071L12.574 10.6142L12.861 9.9212C12.6165 9.82 12.3691 9.78288 12.1218 9.76601C11.8811 9.74959 11.5892 9.75 11.25 9.75V10.5ZM12.574 10.6142L12.287 11.307C12.4708 11.3832 12.6168 11.5292 12.693 11.713L13.3858 11.426L14.0787 11.1389C13.8504 10.5877 13.4123 10.1496 12.8611 9.92126L12.574 10.6142ZM13.3858 11.426L12.6929 11.7129C12.7058 11.7441 12.7258 11.8093 12.7375 11.9803C12.7496 12.158 12.75 12.3903 12.75 12.75H13.5H14.25C14.25 12.4108 14.2504 12.1189 14.234 11.8782C14.2171 11.6309 14.18 11.3835 14.0788 11.139L13.3858 11.426ZM13.5 12.75H12.75C12.75 13.1097 12.7496 13.342 12.7375 13.5197C12.7258 13.6907 12.7058 13.7559 12.6929 13.7871L13.3858 14.074L14.0788 14.361C14.18 14.1165 14.2171 13.8691 14.234 13.6218C14.2504 13.3811 14.25 13.0892 14.25 12.75H13.5ZM13.3858 14.074L12.693 13.787C12.6168 13.9708 12.4708 14.1168 12.287 14.193L12.574 14.8858L12.8611 15.5787C13.4123 15.3504 13.8504 14.9123 14.0787 14.3611L13.3858 14.074ZM12.574 14.8858L12.2871 14.1929C12.2559 14.2058 12.1907 14.2258 12.0197 14.2375C11.842 14.2496 11.6097 14.25 11.25 14.25V15V15.75C11.5892 15.75 11.8811 15.7504 12.1218 15.734C12.3691 15.7171 12.6165 15.68 12.861 15.5788L12.574 14.8858ZM11.25 15V14.25C10.8903 14.25 10.658 14.2496 10.4803 14.2375C10.3093 14.2258 10.2441 14.2058 10.2129 14.1929L9.92595 14.8858L9.63904 15.5788C9.88346 15.68 10.1309 15.7171 10.3782 15.734C10.6189 15.7504 10.9108 15.75 11.25 15.75V15ZM9.92595 14.8858L10.213 14.193C10.0292 14.1168 9.88317 13.9708 9.80704 13.787L9.11415 14.074L8.42126 14.3611C8.64963 14.9123 9.08766 15.3504 9.6389 15.5787L9.92595 14.8858ZM9.11415 14.074L9.8071 13.7871C9.79415 13.7559 9.77419 13.6907 9.76253 13.5197C9.75041 13.342 9.75 13.1097 9.75 12.75H9H8.25C8.25 13.0892 8.24959 13.3811 8.26601 13.6218C8.28288 13.8691 8.32 14.1165 8.4212 14.361L9.11415 14.074Z"
                                    fill="currentColor"
                                ></path>
                            </svg>
                            <span>Filter</span>
                        </button>
                    </div>

                    {/* Past-dates notice: the API shifted the search window forward to the next
                        availability and flagged it. */}
                    {data?.datesAdjusted && !datesBannerDismissed && (
                        <div className="mb-6 bg-primary text-white rounded-xl p-4 flex items-start gap-3">
                            <div className="flex-1 text-sm">
                                <span className="font-semibold">
                                    Your search dates were in the past
                                </span>{" "}
                                — we&apos;ve updated your search to the next available dates (
                                {formatDate(
                                    parseDate(data.adjustedEarlyStart, "yyyy-MM-dd", new Date()),
                                    "d MMM yyyy"
                                )}{" "}
                                –{" "}
                                {formatDate(
                                    parseDate(data.adjustedLateStart, "yyyy-MM-dd", new Date()),
                                    "d MMM yyyy"
                                )}
                                ).
                            </div>
                            <button
                                type="button"
                                onClick={() => setDatesBannerDismissed(true)}
                                className="text-white/80 hover:text-white font-semibold px-2"
                                aria-label="Dismiss"
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    <div className="mb-6 bg-white px-4 py-2.5 rounded-xl border border-content_4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                                    Prices
                                </span>
                                <span
                                    className={
                                        "text-sm font-semibold transition-colors duration-200 " +
                                        (pricingDisplayMethod == "pp"
                                            ? "text-primary"
                                            : "text-gray-400")
                                    }
                                >
                                    Per Person
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-label="Show prices as total instead of per person"
                                    aria-checked={pricingDisplayMethod == "total"}
                                    onClick={() =>
                                        setPricingDisplayMethod(
                                            pricingDisplayMethod == "pp" ? "total" : "pp"
                                        )
                                    }
                                    className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary transition-colors duration-200 ease-in-out focus:outline-none"
                                >
                                    <span
                                        className={
                                            "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out " +
                                            (pricingDisplayMethod == "total"
                                                ? "translate-x-4"
                                                : "translate-x-0")
                                        }
                                    />
                                </button>
                                <span
                                    className={
                                        "text-sm font-semibold transition-colors duration-200 " +
                                        (pricingDisplayMethod == "total"
                                            ? "text-primary"
                                            : "text-gray-400")
                                    }
                                >
                                    Total
                                </span>
                            </div>

                            <div className="flex items-center gap-2 sm:ml-auto">
                                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                                    Sort by
                                </span>
                                <div className="relative w-full sm:w-[16.25rem]">
                                    <select
                                        className="sort-dropdown"
                                        value={data?.sort}
                                        onChange={(e) => setValue("sort", e.target.value)}
                                    >
                                        {data &&
                                            data.sortingMethods &&
                                            data.sortingMethods.map((x, i) => {
                                                return (
                                                    <option key={x.id} value={x.id}>
                                                        {x.name}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-content_2">
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 18 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.25 4.5V13.5M5.25 4.5C5.84291 4.50001 7.5 6.74998 7.5 6.74998M5.25 4.5C4.65708 4.49999 3 6.75 3 6.75M12.75 12.75V4.5M15 11.25C15 11.25 13.3429 13.5 12.75 13.5C12.157 13.5 10.5 11.25 10.5 11.25"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LoadingWrapper isLoading={!data || isLoadingFiltering}>
                        {data && data.offers && data.offers.length > 0
                            ? data.offers.map((x, i) => {
                                  return <div key={i}>{resultTemplate(x)}</div>;
                              })
                            : data &&
                              data.offers &&
                              data.offers.length === 0 && (
                                  <div className="bg-white p-8 rounded-xl border border-content_4 text-center">
                                      <div className="max-w-md mx-auto">
                                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                              No Results Found
                                          </h3>
                                          <p className="text-gray-600 mb-4">
                                              We couldn’t find a matching holiday for your current
                                              search, but adjusting your dates or filters may reveal
                                              more options.
                                          </p>
                                          <p className="text-gray-600 mb-4">
                                              Prefer to speak with someone? Call {SALES_PHONE}, our
                                              team are on hand to find you alternatives.
                                          </p>
                                      </div>
                                  </div>
                              )}
                        {hasMore && (
                            <div ref={infiniteRef} className="text-center">
                                <ThreeDot
                                    color="#009DD6"
                                    size="large"
                                    text="Loading"
                                    textColor="#009DD6"
                                    easing="ease-in-out"
                                />
                            </div>
                        )}
                    </LoadingWrapper>
                </div>
            </div>
            {debugMode && data && (
                <div className="fixed bottom-0 right-0 left-0 z-40 text-center">
                    {withdrawnOpen && data.offersWithdrawn?.length > 0 && (
                        <div className="max-h-[40vh] overflow-y-auto bg-gray-800 text-white text-left text-sm px-4 py-2">
                            {data.offersWithdrawn.map((cpr, i) => (
                                <div key={i} className="py-0.5">
                                    <span className="font-semibold">CPR {cpr}</span> —{" "}
                                    {data.withdrawnReasons?.[cpr] ?? "No reason recorded"}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="w-full bg-gray-600 flex text-white">
                        <button
                            type="button"
                            className="flex-auto disabled:cursor-default"
                            disabled={!data.offersWithdrawn?.length}
                            onClick={() => setWithdrawnOpen(!withdrawnOpen)}
                        >
                            {data.offersWithdrawn?.length ?? 0} hotel
                            {data.offersWithdrawn?.length === 1 ? "" : "s"} withdrawn (of{" "}
                            {(data.offers?.length ?? 0) + (data.offersWithdrawn?.length ?? 0)}{" "}
                            loaded)
                            {data.offersWithdrawn?.length > 0 && (withdrawnOpen ? " ▾" : " ▴")}
                        </button>
                        <div
                            className={
                                "flex-auto " +
                                (data.searchSource === "Almanac" ? "bg-green-600" : "bg-gray-700")
                            }
                        >
                            Search source: {data.searchSource ?? "unknown"}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
